import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { Box, Typography, Card, CardContent, IconButton, useMediaQuery, useTheme } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import styles from "./WeeklyCalendar.module.css";
import {
  getDayName,
  getWeekStart,
  getWeekDates,
  formatDateToString,
  isToday,
  isSameDay,
  IWeeklyCalendarProps,
  ICalendarEvent,
} from "./CalendarUtils";

/**
 * Generate time slots for the calendar
 */
const generateTimeSlots = (startHour = 0, endHour = 24): string[] => {
  const slots: string[] = [];
  for (let hour = startHour; hour < endHour; hour++) {
    slots.push(`${String(hour).padStart(2, "0")}:00`);
  }
  return slots;
};

/**
 * Reusable WeeklyCalendar component with time slots
 * Features:
 * - Weekly schedule view with time slots on the left
 * - 8:00-17:00 focused range
 * - Romanian localization
 * - Event display in time slots
 * - Optimized with useMemo and useCallback
 */
export default function WeeklyCalendar({
  events = [],
  onDateSelect,
  onEventClick,
  initialDate = new Date(),
  className,
  currentDate: externalCurrentDate,
  timeSlots: customTimeSlots,
  showTimeSlots = true,
  renderEventCard,
}: IWeeklyCalendarProps) {
  const [internalCurrentDate] = useState(initialDate);
  const currentDate = externalCurrentDate || internalCurrentDate;

  const [selectedTimeSlot, setSelectedTimeSlot] = useState<{
    date: Date;
    time: string;
  } | null>(null);

  // State for single day view on mobile/tablet
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);

  // Detect mobile/tablet screens
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md')); // 900px and below

  // Get week start date (Monday) - memoized
  const weekStart = useMemo(() => getWeekStart(currentDate), [currentDate]);

  // Get all dates for the week - memoized
  const weekDates = useMemo(() => getWeekDates(weekStart), [weekStart]);

  // Get dates to display based on screen size
  const displayDates = useMemo(() => {
    if (isMobileOrTablet) {
      return [weekDates[selectedDayIndex]];
    }
    return weekDates;
  }, [weekDates, selectedDayIndex, isMobileOrTablet]);

  // Update selected day index when current date changes
  useEffect(() => {
    if (isMobileOrTablet) {
      // Find the index of today in the week, default to 0 if not found
      const todayIndex = weekDates.findIndex(date => isToday(date));
      setSelectedDayIndex(todayIndex >= 0 ? todayIndex : 0);
    }
  }, [currentDate, weekDates, isMobileOrTablet]);

  // Day names array - memoized
  const dayNames = useMemo(
    () => [1, 2, 3, 4, 5, 6, 0].map((dayIndex) => getDayName(dayIndex)),
    []
  );

  // Generate all time slots (full day 0-24) - memoized
  const allTimeSlots = useMemo(
    () => customTimeSlots || generateTimeSlots(0, 24),
    [customTimeSlots]
  );

  // Reference to the focus time slot (8:00) for scrolling
  const focusTimeRef = useRef<HTMLDivElement>(null);

  // Scroll to focus time (8:00) on mount
  useEffect(() => {
    if (focusTimeRef.current) {
      focusTimeRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, []);

  // Navigate between days on mobile/tablet
  const handleDayNavigate = (direction: 'prev' | 'next') => {
    setSelectedDayIndex(prev => {
      if (direction === 'prev') {
        return Math.max(0, prev - 1);
      } else {
        return Math.min(weekDates.length - 1, prev + 1);
      }
    });
  };

  /**
   * Get events for a specific date and time slot
   */
  const getEventsForTimeSlot = useCallback(
    (date: Date, timeSlot: string): ICalendarEvent[] => {
      return events.filter((event) => {
        if (event.startDate) {
          const eventStart = new Date(event.startDate);
          const [hours, minutes] = timeSlot.split(":");
          const slotDate = new Date(date);
          slotDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);

          return (
            formatDateToString(slotDate) === formatDateToString(eventStart) &&
            slotDate.getHours() === eventStart.getHours()
          );
        }

        const dateStr = formatDateToString(date);
        if (!event.date.startsWith(dateStr)) return false;
        if (event.date.includes(timeSlot)) return true;
        return false;
      });
    },
    [events]
  );

  /**
   * Calculate event height in pixels based on duration
   * Each hour slot is 100px (desktop), 80px (tablet), 70px (mobile), 65px (extra small)
   */
  const calculateEventHeight = useCallback((event: ICalendarEvent): number => {
    if (!event.startDate || !event.endDate) return isMobileOrTablet ? 80 : 100;

    const start = new Date(event.startDate);
    const end = new Date(event.endDate);
    const durationMinutes = (end.getTime() - start.getTime()) / (1000 * 60);

    // Determine slot height based on screen size
    const slotHeight = isMobileOrTablet ? 80 : 100;

    // Calculate height proportionally: (durationMinutes / 60) * slotHeight
    return (durationMinutes / 60) * slotHeight;
  }, [isMobileOrTablet]);

  /**
   * Get events that are visible in a time slot (for rendering purposes)
   */
  const getEventsInSlot = useCallback((date: Date, timeSlot: string): ICalendarEvent[] => {
    const [hours] = timeSlot.split(":");
    const slotStart = new Date(date);
    slotStart.setHours(parseInt(hours), 0, 0, 0);
    const slotEnd = new Date(slotStart);
    slotEnd.setHours(slotStart.getHours() + 1);

    return events.filter((event) => {
      if (!event.startDate || !event.endDate) return false;

      const eventStart = new Date(event.startDate);
      const eventEnd = new Date(event.endDate);

      // Check if event is visible in this time slot
      return (
        formatDateToString(eventStart) === formatDateToString(date) &&
        eventStart < slotEnd &&
        eventEnd > slotStart
      );
    });
  }, [events]);

  /**
   * Check if two events actually overlap (not just back-to-back)
   */
  const eventsOverlap = (eventA: ICalendarEvent, eventB: ICalendarEvent): boolean => {
    if (!eventA.startDate || !eventA.endDate || !eventB.startDate || !eventB.endDate) return false;

    const aStart = new Date(eventA.startDate).getTime();
    const aEnd = new Date(eventA.endDate).getTime();
    const bStart = new Date(eventB.startDate).getTime();
    const bEnd = new Date(eventB.endDate).getTime();

    // Events overlap if one starts BEFORE the other ends (not at the same time)
    return aStart < bEnd && aEnd > bStart;
  };

  /**
   * Get events that actually overlap with a specific event
   */
  const getOverlappingEvents = useCallback((event: ICalendarEvent, date: Date): ICalendarEvent[] => {
    if (!event.startDate || !event.endDate) return [event];

    const dayEvents = events.filter((e) => {
      if (!e.startDate) return false;
      const eventDate = new Date(e.startDate);
      return formatDateToString(eventDate) === formatDateToString(date);
    });

    return dayEvents.filter((e) => eventsOverlap(event, e));
  }, [events]);


  /**
   * Check if a time slot is in the past
   */
  const isPastTimeSlot = useCallback((date: Date, timeSlot: string): boolean => {
    const now = new Date();
    const [hours, minutes] = timeSlot.split(":");
    const slotDateTime = new Date(date);
    slotDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    return slotDateTime < now;
  }, []);

  /**
   * Handle time slot click
   */
  const handleTimeSlotClick = useCallback(
    (date: Date, timeSlot: string) => {
      // Prevent clicking on past time slots
      if (isPastTimeSlot(date, timeSlot)) {
        return;
      }

      const dateEvents = getEventsForTimeSlot(date, timeSlot);
      setSelectedTimeSlot({ date, time: timeSlot });
      onDateSelect?.(date, dateEvents, timeSlot);
    },
    [getEventsForTimeSlot, onDateSelect, isPastTimeSlot]
  );

  /**
   * Render time slot cell
   */
  const renderTimeSlotCell = useCallback(
    (date: Date, timeSlot: string) => {
      const slotEvents = getEventsForTimeSlot(date, timeSlot);
      const eventsInSlot = getEventsInSlot(date, timeSlot);
      const hasEvents = slotEvents.length > 0;
      const isSelected =
        selectedTimeSlot &&
        isSameDay(date, selectedTimeSlot.date) &&
        timeSlot === selectedTimeSlot.time;
      const isTodayDate = isToday(date);
      const isPast = isPastTimeSlot(date, timeSlot);

      return (
        <Box
          key={`${formatDateToString(date)}-${timeSlot}`}
          onClick={() => handleTimeSlotClick(date, timeSlot)}
          className={`${styles.timeSlotCell} ${hasEvents ? styles.hasEvents : ""} ${
            isSelected ? styles.selected : ""
          } ${isTodayDate ? styles.today : ""} ${isPast ? styles.disabled : ""}`}
        >
          {hasEvents && (() => {
            const maxVisible = 2;
            const visibleEvents = slotEvents.slice(0, maxVisible);
            const hiddenCount = slotEvents.length - maxVisible;

            // Slot height for calculating minute offset
            const slotHeight = isMobileOrTablet ? 80 : 100;

            return (
              <>
                {visibleEvents.map((event) => {
                  const eventHeight = calculateEventHeight(event);
                  const eventId = event._id || event.id;

                  // Get actual overlapping events for this specific event
                  const actualOverlapping = getOverlappingEvents(event, date);
                  const overlapCount = Math.min(actualOverlapping.length, maxVisible);

                  // Find event position among its overlapping events
                  const eventIndex = actualOverlapping.findIndex(e => (e._id || e.id) === eventId);

                  // Calculate width based on actual overlapping events
                  const columnWidth = 100 / overlapCount;
                  const leftOffset = eventIndex * columnWidth;

                  // Calculate top offset based on event start minute within the hour
                  let topOffset = 2;
                  if (event.startDate) {
                    const eventStart = new Date(event.startDate);
                    const minutesIntoHour = eventStart.getMinutes();
                    topOffset = (minutesIntoHour / 60) * slotHeight + 2;
                  }

                  return (
                    <Box
                      key={eventId}
                      className={styles.eventBlock}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEventClick?.(event);
                      }}
                      style={{
                        position: 'absolute',
                        top: `${topOffset}px`,
                        left: `calc(${leftOffset}% + 2px)`,
                        width: `calc(${columnWidth}% - 4px)`,
                        height: `${eventHeight - 4}px`,
                        zIndex: 2 + eventIndex,
                      }}
                    >
                      {renderEventCard ? (
                        renderEventCard(event, overlapCount)
                      ) : (
                        <Typography variant="caption" className={styles.eventTitle}>
                          {event.title}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
                {hiddenCount > 0 && (
                  <Box
                    className={styles.moreEventsIndicator}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDateSelect?.(date, eventsInSlot, timeSlot);
                    }}
                  >
                    <Typography variant="caption">
                      +{hiddenCount}
                    </Typography>
                  </Box>
                )}
              </>
            );
          })()}
        </Box>
      );
    },
    [
      getEventsForTimeSlot,
      getEventsInSlot,
      getOverlappingEvents,
      calculateEventHeight,
      handleTimeSlotClick,
      selectedTimeSlot,
      onEventClick,
      renderEventCard,
      isPastTimeSlot,
    ]
  );

  return (
    <Card className={`${styles.weeklyCalendarCard} ${className || ""}`}>
      <CardContent className={styles.calendarContent}>
        {/* Mobile/Tablet Day Navigation */}
        {isMobileOrTablet && (
          <>
            <Box className={styles.mobileDayNavigation}>
              <IconButton
                onClick={() => handleDayNavigate('prev')}
                disabled={selectedDayIndex === 0}
                className={styles.dayNavButton}
                size="small"
              >
                <ChevronLeft />
              </IconButton>
              <Box className={styles.mobileDayInfo}>
                <Typography variant="body2" className={styles.mobileDayName}>
                  {dayNames[selectedDayIndex]}
                </Typography>
                <Typography variant="body1" className={styles.mobileDayDate}>
                  {displayDates[0]?.getDate()} {displayDates[0]?.toLocaleDateString('ro-RO', { month: 'long' })}
                </Typography>
              </Box>
              <IconButton
                onClick={() => handleDayNavigate('next')}
                disabled={selectedDayIndex === weekDates.length - 1}
                className={styles.dayNavButton}
                size="small"
              >
                <ChevronRight />
              </IconButton>
            </Box>

            {/* Horizontal Day Tabs for Quick Selection */}
            <Box className={styles.dayTabsContainer}>
              {weekDates.map((date, index) => {
                const isTodayDate = isToday(date);
                const isSelected = index === selectedDayIndex;
                return (
                  <Box
                    key={index}
                    onClick={() => setSelectedDayIndex(index)}
                    className={`${styles.dayTab} ${isSelected ? styles.dayTabSelected : ''} ${isTodayDate ? styles.dayTabToday : ''}`}
                  >
                    <Typography variant="caption" className={styles.dayTabName}>
                      {dayNames[index].slice(0, 3)}
                    </Typography>
                    <Typography variant="caption" className={styles.dayTabDate}>
                      {String(date.getDate()).padStart(2, '0')}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </>
        )}

        {/* Header with day names and dates */}
        <Box className={`${styles.calendarHeader} ${isMobileOrTablet ? styles.mobileHeader : ''}`}>
          {/* Empty corner cell for time column */}
          <Box className={styles.timeColumnHeader} />

          {/* Day headers */}
          {displayDates.map((date, index) => {
            const isTodayDate = isToday(date);
            const originalIndex = isMobileOrTablet ? selectedDayIndex : index;
            return (
              <Box
                key={index}
                className={`${styles.dayHeaderCell} ${
                  isTodayDate ? styles.todayHeader : ""
                }`}
              >
                <Typography variant="caption" className={styles.dayName}>
                  {dayNames[originalIndex]}
                </Typography>
                <Typography
                  variant="h6"
                  className={`${styles.dayDate} ${
                    isTodayDate ? styles.todayNumber : ""
                  }`}
                >
                  {date.getDate()}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Time slots grid - All 24 hours */}
        <Box className={styles.scheduleGrid}>
          {allTimeSlots.map((timeSlot) => {
            const hour = parseInt(timeSlot.split(":")[0]);
            const isFocusHour = hour === 8; // Focus on 8:00

            return (
              <Box
                key={timeSlot}
                className={`${styles.timeRow} ${isMobileOrTablet ? styles.mobileTimeRow : ''}`}
                ref={isFocusHour ? focusTimeRef : null}
              >
                {/* Time label */}
                <Box className={styles.timeLabel}>
                  <Typography variant="body2" className={styles.timeText}>
                    {timeSlot}
                  </Typography>
                </Box>

                {/* Time slot cells for each day */}
                {displayDates.map((date) => renderTimeSlotCell(date, timeSlot))}
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
}